import React, { useState } from "react";
import AnalysisPanel, { PanelEmpty } from "../AnalysisPanel";
import { getCiReportEventSpikes } from "../../../api/competitiveIntelligence";
import { InferredOccupancy } from "../../../utils/ciFormat";
import ThinScroll from "../../shared/ThinScroll";
import SelectMenu from "../../shared/SelectMenu";
import { PanelTakeaway, fmtDate, fmtRate, fmtPct } from "../shared";
import { getDemandSignalTone, DEMAND_SIGNAL_TONE_CLASSES } from "../shared";

// Event spikes render the same demand-signal vocabulary as Demand Signals
// (HIGH DEMAND, ELEVATED, RESTRICTED, NORMAL, plus MAJOR EVENT). Tone follows
// the shared keyword-regex classifier so HIGH DEMAND is red, not emerald.
const SignalBadge = ({ signal }) => {
  const cls = DEMAND_SIGNAL_TONE_CLASSES[getDemandSignalTone(signal)].badge;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] uppercase tracking-wide font-semibold ${cls}`}>
      {signal || "—"}
    </span>
  );
};

// Row tint mirrors the badge tone so high-demand rows stand out at a glance.
const rowTintFor = (signal) => DEMAND_SIGNAL_TONE_CLASSES[getDemandSignalTone(signal)].row;

const EventSpikesBody = ({ rows, threshold, onThresholdChange }) => (
  <div>
    <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800 flex items-center gap-3 flex-wrap">
      <span className="inline-flex items-center gap-1.5">
        <span className="text-sm text-gray-600 dark:text-gray-300">Threshold:</span>
        <SelectMenu
          ariaLabel="Spike threshold"
          value={threshold}
          onChange={onThresholdChange}
          options={[
            { value: 5, label: "5% above baseline" },
            { value: 10, label: "10% above baseline" },
            { value: 15, label: "15% above baseline" },
            { value: 20, label: "20% above baseline" },
          ]}
        />
      </span>
      <span className="text-xs text-gray-500 dark:text-gray-400">
        {rows.length} {rows.length === 1 ? "date" : "dates"} flagged
      </span>
    </div>
    {rows.length === 0 ? (
      <PanelEmpty message="No price spikes above the selected threshold." />
    ) : (
      <ThinScroll className="overflow-x-auto responsive-card-table-wrapper">
        <table className="w-full text-sm responsive-card-table">
          <thead className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
            <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
              <th className="px-3 py-2.5 text-left font-semibold">Date</th>
              <th className="px-3 py-2.5 text-left font-semibold">Window</th>
              <th className="px-3 py-2.5 text-right font-semibold">Days Out</th>
              <th className="px-3 py-2.5 text-right font-semibold">Avg Rate</th>
              <th className="px-3 py-2.5 text-right font-semibold">Baseline</th>
              <th className="px-3 py-2.5 text-right font-semibold">Spike</th>
              <th className="px-3 py-2.5 text-right font-semibold">Your Rate</th>
              <th className="px-3 py-2.5 text-right font-semibold whitespace-nowrap">Limited Avail.</th>
              <th className="px-3 py-2.5 text-right font-semibold">Comp Occupancy</th>
              <th className="px-3 py-2.5 text-left font-semibold">Signal</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const tint = rowTintFor(row.event_signal);
              return (
                <tr key={`${row.check_in}-${index}`} className={`border-b dark:border-gray-800 transition ${tint}`}>
                  <td data-label="Date" className="px-3 py-2.5 font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">{fmtDate(row.check_in)}</td>
                  <td data-label="Window" className="px-3 py-2.5 text-gray-600 dark:text-gray-300 whitespace-nowrap">{row.date_window_name || "—"}</td>
                  <td data-label="Days Out" className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">{row.days_out ?? "—"}</td>
                  <td data-label="Avg Rate" className="px-3 py-2.5 text-right text-gray-900 dark:text-gray-100 whitespace-nowrap">{fmtRate(row.avg_price)}</td>
                  <td data-label="Baseline" className="px-3 py-2.5 text-right text-gray-500 dark:text-gray-400 whitespace-nowrap">{fmtRate(row.baseline_price)}</td>
                  <td data-label="Spike" className="px-3 py-2.5 text-right font-semibold text-red-600 dark:text-red-400 whitespace-nowrap">
                    <span>
                      +{fmtRate(row.price_spike)} <span className="text-xs">({fmtPct(row.spike_pct)})</span>
                    </span>
                  </td>
                  <td data-label="Your Rate" className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmtRate(row.your_rate)}</td>
                  <td data-label="Limited Avail." className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">{row.sold_out_count ?? 0}</td>
                  <td data-label="Comp Occupancy" className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap"><InferredOccupancy value={row.comp_occupancy} /></td>
                  <td data-label="Signal" className="px-3 py-2.5 whitespace-nowrap"><SignalBadge signal={row.event_signal} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </ThinScroll>
    )}
  </div>
);

const EventSpikesPanel = ({ clientId, categoryCode, say }) => {
  const [threshold, setThreshold] = useState(10);

  return (
    <AnalysisPanel
      id={`event-spikes-${clientId}-${categoryCode || "all"}-${threshold}`}
      title="Event & Price Spikes"
      tip="Dates where the market is pricing well above its own baseline — usually a sign of a holiday, festival, or local event you can charge for. MAJOR EVENT rows are the biggest spikes."
      description="Dates where market pricing exceeds baseline. MAJOR EVENT rows highlighted in red."
      badge={categoryCode ? categoryCode.toUpperCase() : null}
      fetcher={() => getCiReportEventSpikes(clientId, { categoryCode: categoryCode || "rv", thresholdPct: threshold })}
      deps={[clientId, categoryCode, threshold]}
    >
      {({ data }) => {
        const rows = Array.isArray(data) ? [...data].sort((rowA, rowB) => (rowA.check_in || "").localeCompare(rowB.check_in || "")) : [];
        return (
          <>
            <PanelTakeaway panelKey="event_spikes" say={say} />
            <EventSpikesBody rows={rows} threshold={threshold} onThresholdChange={setThreshold} />
          </>
        );
      }}
    </AnalysisPanel>
  );
};

export default EventSpikesPanel;
